import type StoreService from '@ember-data/store';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type Location from 'garaje/models/location';
import type UploaderFactoryService from 'garaje/services/uploader-factory';

import urlBuilder from './url-builder';

interface CreateOptions {
  location: Location;
  model: Entry | Invite;
  store: StoreService;
  /**
   * When true, "block at THIS location" is scoped via the company + a single `locations`
   * (blacklist-locations) entry, because the legacy single `location` rel now produces a company-wide
   * filter. Off (default) keeps the legacy `location` rel.
   */
  useLocationScoping?: boolean;
}

interface SaveOptions {
  blocklistFilter: BlacklistFilter;
  failOnPhotoFetchError: boolean;
  photo: File | null;
  uploaderFactory: UploaderFactoryService;
}

/**
 * Creates a new blocklist filter record from an entry or invite.
 */
export function createBlocklistFilter(options: CreateOptions): BlacklistFilter {
  const { location, model, store, useLocationScoping } = options;

  // Extract phone numbers from userData
  const phoneNumbers = extractPhoneNumber(model);
  // Extract email
  const emails = [];
  if (options.model.email) {
    emails.push(options.model.email);
  }

  // Extract photo (prefer thumbnails.large, fallback to passportAvatar)
  let photoDataUrl = null;

  if (model instanceof Invite) {
    if (model.photoUrl) {
      photoDataUrl = model.photoUrl;
    }
  }

  if (model instanceof Entry) {
    if (model.thumbnails?.original) {
      photoDataUrl = model.thumbnails.original;
    } else if (model.thumbnails?.large) {
      photoDataUrl = model.thumbnails.large;
    } else if (model.passportAvatar) {
      photoDataUrl = model.passportAvatar;
    }
  }

  const sharedAttrs = {
    fullName: model.fullName || '',
    emails,
    phoneNumbers,
    photo: {
      original: null,
      thumb: null,
      dataUrl: photoDataUrl,
    },
  };

  if (useLocationScoping) {
    // Company-scoped with a single blacklist-locations entry keyed by the location id (peek-or-push).
    const company = location.belongsTo('company').value();
    // Fail loud: a null company (relationship not sideloaded) would POST a mis-scoped, company-less
    // filter while `scope` still reads specific_locations. Callers load it via the route.
    if (!company) {
      throw new Error(
        '[blocklist] createBlocklistFilter: location.company is not loaded — sideload the company ' +
          'relationship before creating a location-scoped filter.',
      );
    }
    const blacklistLocation =
      store.peekRecord('blacklist-location', location.id) ??
      store.push({ data: { id: location.id, type: 'blacklist-location' } });
    return store.createRecord('blacklist-filter', {
      ...sharedAttrs,
      company,
      locations: [blacklistLocation],
    });
  }

  // Legacy (flag off): the single `location` rel.
  return store.createRecord('blacklist-filter', {
    ...sharedAttrs,
    location,
  });
}

/**
 * Saves a blocklist filter and its photo if it has one.
 */
export async function saveBlocklistFilter(options: SaveOptions): Promise<void> {
  const { blocklistFilter, failOnPhotoFetchError, uploaderFactory } = options;
  let { photo } = options;
  const isNew = Boolean(blocklistFilter.isNew);

  if (isNew && blocklistFilter.photo?.dataUrl && !photo) {
    try {
      // cache: 'no-store' avoids a CORS failure when the browser has cached
      // a prior no-cors <img> load of the same URL (which S3 returns without
      // an Access-Control-Allow-Origin header). See SQ-7049.
      const blob = await (await fetch(<string>blocklistFilter.photo.dataUrl, { cache: 'no-store' })).blob();
      photo = new File([blob], 'photo.png', { type: 'image/png' });
    } catch (_e) {
      if (failOnPhotoFetchError) {
        throw new Error('Blocklist photo upload failed. Please try again.');
      }
    }
  }

  await blocklistFilter.save();

  if (photo) {
    const uploader = uploaderFactory.createUploader({
      url: urlBuilder.v3.blocklistFilter.photoUpload(blocklistFilter.id),
      method: 'POST',
      headers: { Accept: 'application/vnd.api+json' },
    });
    await uploader.upload(photo);
  }
}

/**
 * Extracts phone numbers from an entry or invite's userData.
 */
export function extractPhoneNumber(model: Entry | Invite): string[] {
  const phoneNumbers: string[] = [];

  // Search userData for phone number fields
  const userData = model.userData || [];
  for (const userDatum of userData) {
    const fieldLabel = userDatum.field?.toLowerCase() ?? '';
    if (fieldLabel.includes('phone') || fieldLabel === 'phonenumber' || fieldLabel === 'your phone number') {
      const phoneValue = userDatum.value;
      if (phoneValue && typeof phoneValue === 'string') {
        phoneNumbers.push(phoneValue);
      }
    }
  }

  return phoneNumbers;
}
